import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mountain, ArrowRight, Check, MapPin, Home, Shield, Star, BarChart3, Users, Globe } from 'lucide-react';
import StateSelector from '../components/StateSelector';
import HimalayanPeaksFooter from '../components/HimalayanPeaksFooter';

// Sample data — district-wise homestay counts for Himachal Pradesh
const DISTRICTS = [
  { id: 'kullu', name: 'Kullu', homestays: 620, valleys: ['Tirthan', 'Parvati', 'Manali'], altitude: '1,200–4,000m', highlight: true },
  { id: 'shimla', name: 'Shimla', homestays: 580, valleys: ['Shimla', 'Mashobra', 'Kufri'], altitude: '1,500–2,800m', highlight: false },
  { id: 'kangra', name: 'Kangra', homestays: 510, valleys: ['Dharamshala', 'Palampur', 'Bir Billing'], altitude: '700–2,500m', highlight: false },
  { id: 'mandi', name: 'Mandi', homestays: 340, valleys: ['Mandi', 'Sundernagar', 'Jogindernagar'], altitude: '800–3,200m', highlight: false },
  { id: 'chamba', name: 'Chamba', homestays: 290, valleys: ['Chamba', 'Dalhousie', 'Khajjiar'], altitude: '900–3,600m', highlight: false },
  { id: 'solan', name: 'Solan', homestays: 260, valleys: ['Kasauli', 'Solan', 'Nahan'], altitude: '600–2,100m', highlight: false },
  { id: 'sirmaur', name: 'Sirmaur', homestays: 180, valleys: ['Nahan', 'Renuka', 'Rajgarh'], altitude: '400–3,000m', highlight: false },
  { id: 'kinnaur', name: 'Kinnaur', homestays: 310, valleys: ['Sangla', 'Kalpa', 'Chitkul'], altitude: '2,300–6,500m', highlight: true },
  { id: 'lahaul', name: 'Lahaul-Spiti', homestays: 380, valleys: ['Spiti', 'Lahaul', 'Kaza'], altitude: '3,000–5,000m', highlight: true },
  { id: 'hamirpur', name: 'Hamirpur', homestays: 120, valleys: ['Hamirpur', 'Nadaun'], altitude: '400–1,100m', highlight: false },
  { id: 'una', name: 'Una', homestays: 90, valleys: ['Una', 'Amb'], altitude: '350–900m', highlight: false },
  { id: 'bilaspur', name: 'Bilaspur', homestays: 110, valleys: ['Bilaspur', 'Gobind Sagar'], altitude: '500–1,400m', highlight: false },
];

const TOTAL_HOMESTAYS = DISTRICTS.reduce((sum, d) => sum + d.homestays, 0);

const TIERS = [
  {
    name: 'Registered',
    tagline: 'State-Licensed Homestays',
    desc: 'Every homestay registered with the Himachal Pradesh Tourism Department, digitized and made discoverable. Basic listing with location, contact, and license verification.',
    icon: Home,
    color: '#8B9DAF',
    features: ['License verification badge', 'Location on district map', 'Basic contact listing', 'Searchable by valley & altitude'],
  },
  {
    name: 'Silver',
    tagline: 'GoHimalayan Quality Assured',
    desc: 'Registered homestays that have been editorially visited and quality-verified by the GoHimalayan team. Verified photos, accurate descriptions, and traveller-ready listings.',
    icon: Shield,
    color: '#A8B8C8',
    features: ['On-ground quality verification', 'Professional photography', 'AI-curated descriptions', 'Review management', 'Priority in search results'],
  },
  {
    name: 'Gold',
    tagline: 'The GoHimalayan Collective',
    desc: 'Premium partners with full storytelling pages, active marketing campaigns, direct WhatsApp booking, and dedicated account management. The best of Himalayan hospitality.',
    icon: Star,
    color: '#D4A853',
    features: ['Luxury storytelling page', 'Direct booking integration', 'Social media marketing', 'Revenue analytics dashboard', 'Dedicated account manager', 'Featured in campaigns'],
  },
];

const VALUE_PROPS = [
  { icon: Globe, title: 'Digital Registry', desc: 'Every licensed homestay in Himachal — searchable, verified, bookable. One platform for the entire state.' },
  { icon: BarChart3, title: 'Demand Intelligence', desc: 'Real-time traveller search data, seasonal demand patterns, and valley-level occupancy insights for policy planning.' },
  { icon: Users, title: 'Host Enablement', desc: 'Training, photography, and digital literacy programs for homestay owners — upgrading the grassroots hospitality ecosystem.' },
  { icon: Shield, title: 'Quality Assurance', desc: 'On-ground verification framework that maintains standards while preserving the authentic character of Himalayan homestays.' },
];

// SVG Map of Himachal Pradesh — simplified district boundaries
function HimachalMap({ activeDistrict, setActiveDistrict }) {
  const districtPaths = {
    'lahaul': { d: 'M180 20 L280 15 L320 60 L300 120 L240 130 L190 100 Z', cx: 250, cy: 70 },
    'kinnaur': { d: 'M300 120 L380 80 L420 130 L390 180 L320 170 L300 140 Z', cx: 355, cy: 135 },
    'kullu': { d: 'M190 100 L240 130 L300 140 L280 200 L220 210 L170 170 Z', cx: 230, cy: 160 },
    'chamba': { d: 'M40 40 L140 30 L180 20 L190 100 L170 170 L100 160 L50 110 Z', cx: 120, cy: 95 },
    'kangra': { d: 'M50 110 L100 160 L170 170 L160 230 L100 250 L40 210 L30 150 Z', cx: 100, cy: 190 },
    'mandi': { d: 'M170 170 L220 210 L230 270 L180 290 L130 270 L100 250 L160 230 Z', cx: 175, cy: 245 },
    'shimla': { d: 'M220 210 L280 200 L320 170 L370 210 L350 270 L280 290 L230 270 Z', cx: 295, cy: 240 },
    'sirmaur': { d: 'M280 290 L350 270 L400 310 L380 370 L310 360 L270 330 Z', cx: 335, cy: 320 },
    'solan': { d: 'M230 270 L280 290 L270 330 L220 350 L180 320 L180 290 Z', cx: 230, cy: 310 },
    'bilaspur': { d: 'M130 270 L180 290 L180 320 L150 350 L110 330 L100 290 Z', cx: 140, cy: 310 },
    'hamirpur': { d: 'M100 250 L130 270 L100 290 L60 280 L50 250 Z', cx: 90, cy: 270 },
    'una': { d: 'M50 250 L60 280 L100 290 L110 330 L80 360 L30 340 L20 290 Z', cx: 65, cy: 310 },
  };

  return (
    <svg viewBox="0 0 440 400" className="w-full max-w-2xl mx-auto" style={{ filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.3))' }}>
      <defs>
        <linearGradient id="mapGlow" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#2C5F8D" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#8B6914" stopOpacity="0.1" />
        </linearGradient>
      </defs>
      {Object.entries(districtPaths).map(([id, path]) => {
        const district = DISTRICTS.find(d => d.id === id);
        const isActive = activeDistrict === id;
        const isHighlight = district?.highlight;
        return (
          <g key={id}
            onMouseEnter={() => setActiveDistrict(id)}
            onMouseLeave={() => setActiveDistrict(null)}
            style={{ cursor: 'pointer' }}
          >
            <path
              d={path.d}
              fill={isActive ? '#2C5F8D' : isHighlight ? 'rgba(44,95,141,0.35)' : 'rgba(255,255,255,0.12)'}
              stroke={isActive ? '#C19A6B' : 'rgba(255,255,255,0.3)'}
              strokeWidth={isActive ? 2 : 1}
              style={{ transition: 'all 0.3s ease' }}
            />
            <text
              x={path.cx} y={path.cy}
              textAnchor="middle" dominantBaseline="middle"
              fill={isActive ? '#fff' : 'rgba(255,255,255,0.65)'}
              fontSize={isActive ? 12 : 10}
              fontFamily="Georgia, serif"
              style={{ transition: 'all 0.3s ease', pointerEvents: 'none' }}
            >
              {district?.name}
            </text>
            {isActive && (
              <text
                x={path.cx} y={path.cy + 16}
                textAnchor="middle" dominantBaseline="middle"
                fill="#C19A6B"
                fontSize={9}
                fontFamily="Georgia, serif"
                style={{ pointerEvents: 'none' }}
              >
                {district?.homestays} homestays
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// Counter animation
function AnimatedNumber({ target, duration = 2000 }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return <span>{count.toLocaleString()}+</span>;
}

export default function HimachalStatePage() {
  const [activeDistrict, setActiveDistrict] = useState(null);
  const activeData = DISTRICTS.find(d => d.id === activeDistrict);

  return (
    <div className="min-h-screen bg-[#141c24]" data-testid="himachal-state-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#141c24]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <Mountain className="w-5 h-5" />
            <span className="text-sm tracking-[0.15em] uppercase">GoHimalayan</span>
          </Link>
          <StateSelector current="himachal" />
        </div>
      </nav>

      {/* Hero — The Numbers Wall */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1768108072365-28bde53f40e4?w=1920&q=80"
            alt="" className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#141c24]/40 via-[#141c24]/40 to-[#141c24]/95" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          {/* State badge */}
          <div className="inline-flex items-center gap-2 border border-white/20 bg-white/5 backdrop-blur-sm px-5 py-2 rounded-full mb-10">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-white/70 text-[10px] tracking-[0.4em] uppercase">Himachal Pradesh</span>
          </div>

          {/* Large number */}
          <h1 className="text-7xl sm:text-8xl md:text-9xl font-light text-white tracking-tight mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 4px 30px rgba(0,0,0,0.4)' }}>
            <AnimatedNumber target={TOTAL_HOMESTAYS} />
          </h1>

          <p className="text-white/70 text-sm tracking-[0.3em] uppercase mb-12">
            Licensed Homestays &middot; 12 Districts &middot; One Platform
          </p>

          {/* Tagline */}
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed max-w-3xl mx-auto mb-14"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
            Where apple orchards meet ancient temples,<br />and every ridge hides a village worth finding
          </h2>
        </div>
      </section>

      {/* Story Section — Above Living Map */}
      <section className="pt-24 pb-8 px-6 relative" data-testid="story-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#C19A6B] text-[10px] tracking-[0.5em] uppercase mb-6">Pahari Soul</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed mb-8"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            In Himachal, the host doesn't check you in.<br />
            They invite you home.
          </h2>
          <p className="text-white/50 text-sm sm:text-base leading-relaxed max-w-3xl mx-auto">
            Himachal's homestays aren't hotels — they're kitchens where grandmothers cook rajma-chawal, 
            balconies where apple farmers share stories, and courtyards where children play while 
            travellers watch the Dhauladhar turn gold at sunset. When a traveller books a village homestay, 
            the money doesn't leave — it feeds the school, fixes the road, keeps the young from migrating. 
            This is sustainable tourism at its most honest.
          </p>
        </div>
      </section>

      {/* Living Map Section */}
      <section className="py-24 px-6 relative" data-testid="living-map-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C19A6B] text-[10px] tracking-[0.5em] uppercase mb-4">The Living Map</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Every district. Every valley. Every door.
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Map */}
            <div className="relative">
              <HimachalMap activeDistrict={activeDistrict} setActiveDistrict={setActiveDistrict} />
            </div>

            {/* District Info Panel */}
            <div className="space-y-6">
              {activeData ? (
                <div className="transition-all duration-300">
                  <p className="text-[#C19A6B] text-[10px] tracking-[0.4em] uppercase mb-3">District</p>
                  <h3 className="text-4xl text-white font-light italic mb-2"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {activeData.name}
                  </h3>
                  <div className="flex items-baseline gap-3 mb-6">
                    <span className="text-5xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                      {activeData.homestays}
                    </span>
                    <span className="text-white/40 text-sm tracking-wider uppercase">Registered Homestays</span>
                  </div>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-white/40" />
                      <span className="text-white/60 text-sm">{activeData.altitude}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {activeData.valleys.map(v => (
                        <span key={v} className="text-[10px] tracking-wider uppercase px-3 py-1.5 rounded-full border border-white/15 text-white/55">
                          {v}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center lg:text-left">
                  <p className="text-white/20 text-sm italic mb-6">Hover over a district to explore</p>
                  {/* Quick stats */}
                  <div className="grid grid-cols-2 gap-4">
                    {DISTRICTS.filter(d => d.highlight).map(d => (
                      <div key={d.id}
                        className="border border-white/10 rounded-lg p-4 hover:border-white/25 transition-colors cursor-pointer"
                        onMouseEnter={() => setActiveDistrict(d.id)}
                      >
                        <p className="text-white/80 text-sm mb-1">{d.name}</p>
                        <p className="text-2xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                          {d.homestays}
                        </p>
                        <p className="text-white/30 text-[10px] tracking-wider uppercase mt-1">homestays</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Total bar */}
              <div className="border-t border-white/10 pt-6 mt-8">
                <div className="flex items-center justify-between">
                  <span className="text-white/45 text-xs tracking-wider uppercase">Total across Himachal</span>
                  <span className="text-white text-lg font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {TOTAL_HOMESTAYS.toLocaleString()} homestays
                  </span>
                </div>
                <div className="mt-3 h-1 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#2C5F8D] to-[#C19A6B] rounded-full"
                    style={{ width: '100%', animation: 'expandBar 2s ease-out' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Funnel — Registered → Verified → Collective */}
      <section className="py-24 px-6 border-t border-white/10" data-testid="tier-funnel">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C19A6B] text-[10px] tracking-[0.5em] uppercase mb-4">The Homestay Index</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight mb-4"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Three tiers. One ecosystem.
            </h2>
            <p className="text-white/50 text-sm max-w-xl mx-auto leading-relaxed">
              Every homestay begins as Registered. Through quality verification and partnership,
              they rise through the ranks — from Silver to Gold.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TIERS.map((tier, i) => (
              <div key={tier.name}
                className="relative border border-white/10 rounded-xl p-8 hover:border-white/25 transition-all group"
                style={{ background: `linear-gradient(160deg, ${tier.color}12, transparent)` }}
              >
                {/* Tier number */}
                <div className="absolute top-6 right-6 text-white/10 text-6xl font-light"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  {i + 1}
                </div>

                <tier.icon className="w-8 h-8 mb-6" style={{ color: tier.color }} />
                <h3 className="text-xl text-white font-light mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  {tier.name}
                </h3>
                <p className="text-xs tracking-wider uppercase mb-4" style={{ color: tier.color }}>
                  {tier.tagline}
                </p>
                <p className="text-white/45 text-sm leading-relaxed mb-6">{tier.desc}</p>

                <ul className="space-y-2.5">
                  {tier.features.map(f => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: tier.color }} />
                      <span className="text-white/55 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>

                {i < 2 && (
                  <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10">
                    <ArrowRight className="w-5 h-5 text-white/10" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B2G Section — For Tourism Departments */}
      <section className="py-24 px-6 relative overflow-hidden" data-testid="b2g-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#141c24] via-[#182330] to-[#141c24]" />
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-[#C19A6B] text-[10px] tracking-[0.5em] uppercase mb-4">For Tourism Departments</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-white font-light italic tracking-tight mb-6"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              We digitize your homestay registry.<br />
              <span className="text-white/55">You focus on policy.</span>
            </h2>
            <p className="text-white/45 text-sm max-w-2xl mx-auto leading-relaxed">
              GoHimalayan serves as the technology and marketing partner for state tourism departments —
              bringing every licensed homestay online, making them discoverable to millions of travellers,
              and providing real-time demand intelligence for tourism planning.
            </p>
          </div>

          {/* Value Props Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-20">
            {VALUE_PROPS.map(prop => (
              <div key={prop.title} className="border border-white/10 rounded-xl p-8 hover:border-white/20 transition-colors">
                <prop.icon className="w-6 h-6 text-[#C19A6B] mb-5" />
                <h3 className="text-lg text-white font-light mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  {prop.title}
                </h3>
                <p className="text-white/45 text-sm leading-relaxed">{prop.desc}</p>
              </div>
            ))}
          </div>

          {/* The Ask */}
          <div className="border border-[#C19A6B]/20 rounded-xl p-10 sm:p-14 text-center"
            style={{ background: 'linear-gradient(160deg, rgba(193,154,107,0.05), transparent)' }}
          >
            <p className="text-[#C19A6B] text-[10px] tracking-[0.5em] uppercase mb-6">The Partnership Model</p>
            <h3 className="text-2xl sm:text-3xl text-white font-light italic mb-6"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Built to scale. Designed to empower.
            </h3>
            <div className="max-w-2xl mx-auto space-y-4 mb-10">
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#C19A6B]/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#C19A6B] text-xs">1</span>
                </div>
                <p className="text-white/60 text-sm">State shares its licensed homestay registry — GoHimalayan digitizes and maps every property</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#C19A6B]/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#C19A6B] text-xs">2</span>
                </div>
                <p className="text-white/60 text-sm">Travellers discover, compare, and book directly — driving revenue into local communities</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#C19A6B]/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#C19A6B] text-xs">3</span>
                </div>
                <p className="text-white/60 text-sm">State receives real-time demand intelligence, occupancy analytics, and tourism planning dashboards</p>
              </div>
            </div>

            <a href="https://wa.me/919871155963?text=Hi%20GoHimalayan%2C%20I%27d%20like%20to%20discuss%20the%20State%20Partnership%20Program%20for%20Himachal%20Pradesh."
              className="inline-flex items-center gap-3 bg-[#C19A6B] text-[#0a0f14] px-8 py-3.5 rounded-full text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#d4ad7a] transition-colors"
              data-testid="b2g-cta"
              target="_blank" rel="noopener noreferrer"
            >
              Begin Conversation
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <HimalayanPeaksFooter accentColor="#C19A6B" />

      <style>{`
        @keyframes expandBar {
          from { width: 0; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );
}
